import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "components/button";
import agentSrc from "assets/agent.svg";
import goodLuckSrc from "assets/good-luck.svg";
import alexLiveSrc from "assets/alex-live-voyc-colour.svg";
import globalStyles from "index.module.css";
import { useAudio } from "hooks/use-audio";
import { Select } from "components/form/select";
import { paths } from "config/paths";
import { useScreenWidth } from "hooks/use-screen-width";
import { IconButton } from "components/icon-button";
import LeftArrowIcon from "assets/arrow-left.svg?react";
import { Channel, Organisation } from "types";
import { logout } from "api/cookie_auth";

import styles from "./index.module.css";

export type CallMode = "script" | "listener";

export const NewCall = ({
  onStartCall,
  channels,
  handleChannelChange,
  isLoading,
  activeChannelId,
  organisations,
  handleOrganisationChange,
  activeOrganisationId,
  mode,
  onModeChange,
  noActiveScript,
  graphLoadFailed,
}: {
  onStartCall: () => void;
  channels: Channel[];
  handleChannelChange: (value: number | string) => void;
  isLoading: boolean;
  activeChannelId: number | null;
  handleOrganisationChange: (value: number | string) => void;
  organisations: Organisation[];
  activeOrganisationId: number | null;
  mode: CallMode;
  onModeChange: (mode: CallMode) => void;
  noActiveScript: boolean;
  graphLoadFailed: boolean;
}) => {
  const navigate = useNavigate();

  const { isLoading: isLoadingAudio, audioConfig } = useAudio();

  const width = useScreenWidth();

  const isSmallScreen = width < 960;

  const convertToOptions = (items: { id: number; name: string }[]) =>
    items.map((item) => ({
      value: item.id,
      label: item.name,
    }));

  const handleLogout = async () => {
    const [success, error] = await logout();

    if (!success) {
      toast.error(error);
      return;
    }
    navigate(paths.login);
  };

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <img alt="" className={styles.iconAgent} height={80} src={agentSrc} width={80} />
        <img alt="" className={styles.iconGoodluck} height={128} src={goodLuckSrc} width={250} />

        <div className={styles.modeToggle}>
          <button
            type="button"
            className={styles.modeButton}
            data-active={mode === "script"}
            onClick={() => onModeChange("script")}
          >
            Script
          </button>
          <button
            type="button"
            className={styles.modeButton}
            data-active={mode === "listener"}
            onClick={() => onModeChange("listener")}
          >
            Listener
          </button>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            className={styles.loginButton}
            disabled={
              !(channels && audioConfig.inputDevice) ||
              // Script requirements only apply in script mode — listener mode does not need a scorecard.
              (mode === "script" && (noActiveScript || graphLoadFailed))
            }
            isLoading={isLoading || isLoadingAudio}
            onClick={onStartCall}
            type="button"
            variant="orange"
          >
            Start Call
          </Button>
        </div>
        <div className={styles.backButtonContainer}>
          {isSmallScreen ? (
            <IconButton
              onClick={() => {
                navigate(paths.audioSettings);
              }}
              variant="transparent"
            >
              <LeftArrowIcon width={24} height={24} />
            </IconButton>
          ) : (
            <Button
              onClick={() => {
                navigate(paths.audioSettings);
              }}
              type="button"
              variant="white-outline"
            >
              <LeftArrowIcon width={24} height={24} /> Configure Audio
            </Button>
          )}
        </div>
        {organisations?.length > 1 && (
          <div className={styles.orgSelectContainer}>
            <Select
              aria-label="Select a organisation"
              className={styles.select}
              disabled={!organisations}
              emptyMessage="No organisations available"
              id="organisation"
              isLoading={isLoading}
              loadingMessage="Loading organisations..."
              onChange={handleOrganisationChange}
              options={convertToOptions(organisations)}
              placeholder="Select an organisation"
              required
              value={activeOrganisationId ?? ""}
              variant="transparent"
            />
          </div>
        )}
      </div>
      <div className={styles.bottom}>
        <div className={styles.instruction}>
          <p>
            When you&apos;re ready, click the <strong>start call</strong> button. Make sure your{" "}
            <strong>headset</strong> is <strong>connected</strong>.
          </p>
        </div>

        <div className={styles.divide}></div>

        <div className={styles.channelSelectionContainer}>
          <div>Select Channel</div>
          <Select
            aria-label="Select a channel"
            className={styles.select}
            disabled={!channels}
            emptyMessage="No channels available"
            id="channel"
            isLoading={isLoading}
            loadingMessage="Loading channels..."
            onChange={handleChannelChange}
            options={convertToOptions(channels)}
            placeholder="Select a channel"
            required
            value={activeChannelId ?? ""}
          />
        </div>

        <div className={globalStyles.liveLogoContainer}>
          <img height={75} width={200} alt="Voyc" className={styles.logo} src={alexLiveSrc} />
        </div>
      </div>
      <div className={styles.logout}>
        <button type="button" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};
